import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { UAParser } from 'ua-parser-js'

import { BrowserOutdatedModal } from '../global/browserOutdated/BrowserOutdateModal'
import { Button } from '../global/button/Button'
import { useModalsContext } from '../global/context/ModalsContext'
import { useProductReadContext, useProductWriteContext } from '../global/context/ProductContext'
import { useSessionLanguage } from '../global/context/SessionSettingsContext'
import { CampaignAccountConfirmationModal } from '../global/modal/CampaignAccountConfirmationModal'
import { LuckyDrawModal as LuckyDrawModalView } from '../global/modal/LuckyDrawModal'
import { Modal as LegacyModal } from '../global/modal/Modal'
import { ProductContextSwitchModal } from '../global/modal/ProductContextSwitchModal'
import { RegisterProductModal } from '../global/nav/RegisterProductModal'
import { LuckyDrawCampaign } from '../model/LuckyDrawDto'
import { TickmillProductType } from '../model/TickmillProductType'
import { useProductSwitchClick } from '../ui/ProductSwitch/ProductSwitch'
import { Text } from '../ui/Typography/Typography'
import { Modal } from '../ui/v2/Modal'
import { useAccountWriteContext } from '../utils/AccountContextContext'
import { useApiClient } from '../utils/ApiClient'
import { ClientApiClient } from '../utils/clientApi'
import { compareBrowserVersions } from '../utils/compare.utils'
import { isLoginPage as checkIfLoginPage } from '../utils/isLoginPage'
import { outDatedBrowserRoutes, useProductSwitchPath } from '../utils/path'
import { useLocallyPersistedState } from '../utils/useStorage'

interface ModalProps {
  closeModal(): void
}

interface LuckyDrawModalProps extends ModalProps {
  campaign: LuckyDrawCampaign
}

const LuckyDrawModal: React.FC<LuckyDrawModalProps> = (props) => {
  const { campaign, closeModal } = props

  const [isLuckyDrawPlaying, setLuckyDrawPlaying] = useState<boolean>(false)

  if (!campaign) {
    return null
  }

  return (
    <LegacyModal
      closeModal={() => {
        if (!isLuckyDrawPlaying) {
          localStorage.setItem('luckyDrawViewed', 'true')
          closeModal()
        }
      }}
      render={({ closeModal }) => (
        <LuckyDrawModalView
          passSetPlaying={setLuckyDrawPlaying}
          luckyCampaign={campaign}
          onClose={closeModal}
        />
      )}
    />
  )
}

export enum GlobalModalsType {
  ProductSwitch = 100,
  ClientCooolOff,
  RegisterProduct,
  CancelAction,
  CancelSwitch,
  ProductInfo,
  BrowserOutdated,
  DeleteNotification,
  LuckyDraw,
  AddCampaignAccount,
}

export const GlobalModals: React.FC = () => {
  const productSwitchClick = useProductSwitchClick()
  const { activeModal, setActiveModal, closeModal, modalData } = useModalsContext()
  const { changeProductContext } = useProductWriteContext()
  const { isDefaultCFDProductType } = useProductReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const isProductTypeCFD = isDefaultCFDProductType()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const location = useLocation()
  const isLoginPage = checkIfLoginPage(location.pathname)
  const { navigateToNextPath } = useProductSwitchPath()

  const [browserOutdatedDisplay, setBrowserOutdatedDisplay] = useLocallyPersistedState(
    'isBrowserOutdatedModalDisplayed',
    false
  )
  const handleProductSwitch = () => {
    const nextProduct = changeProductContext()
    navigateToNextPath(nextProduct)
  }

  const productSwitchConfirm = () => {
    handleProductSwitch()
    closeModal()
  }

  const registerProduct = async () => {
    const productType = isProductTypeCFD ? TickmillProductType.ETD : TickmillProductType.CFD
    await apiClient.addTickmillProduct(productType)
    closeModal()
    refreshAccount(locale)
    handleProductSwitch()
  }

  useEffect(() => {
    if (browserOutdatedDisplay) {
      return
    }
    const isPageToBeVerified = () => outDatedBrowserRoutes?.includes(location?.pathname)
    if (isLoginPage || !isPageToBeVerified()) {
      return
    }

    const parser = new UAParser()
    const { browser } = parser.getResult()

    if (!(browser.name === 'Safari' || browser.name === 'Mobile Safari')) {
      return
    }
    if (!(compareBrowserVersions(browser.version as string, '15.3') <= 0)) {
      return
    }

    setActiveModal(GlobalModalsType.BrowserOutdated)
    setBrowserOutdatedDisplay(true)
  }, [
    isLoginPage,
    browserOutdatedDisplay,
    setActiveModal,
    setBrowserOutdatedDisplay,
    location?.pathname,
  ])

  switch (activeModal) {
    case GlobalModalsType.ProductSwitch:
      return (
        <Modal.Action
          title={t('Switch from', {
            to: isDefaultCFDProductType() ? 'Futures' : 'CFD',
            from: isDefaultCFDProductType() ? 'CFD' : 'Futures',
          })}
          renderFooter={({ onClose }) => [
            <Button appearance='secondary' onClick={onClose} type='button'>
              {t('Cancel')}
            </Button>,
            <Button appearance='primary' onClick={productSwitchConfirm} type='button'>
              {t('Switch Client Area')}
            </Button>,
          ]}
          onClose={closeModal}
        >
          <ProductContextSwitchModal onConfirm={productSwitchConfirm} onCancel={closeModal} />
        </Modal.Action>
      )
    case GlobalModalsType.LuckyDraw:
      return <LuckyDrawModal {...modalData} closeModal={closeModal} />
    case GlobalModalsType.AddCampaignAccount:
      return <CampaignAccountConfirmationModal onClose={closeModal} />
    case GlobalModalsType.ClientCooolOff:
      return (
        <Modal.Action
          title={t('Restricted Access')}
          renderFooter={({ onClose }) => [
            <Button appearance='primary' onClick={onClose}>
              {t('Got it!')}
            </Button>,
          ]}
          onClose={closeModal}
        >
          <Text>
            {t(
              'Access is restricted to this area due to you failing your appropriateness test 3 times'
            )}
          </Text>
        </Modal.Action>
      )
    case GlobalModalsType.ProductInfo:
      return (
        <Modal.Action
          renderFooter={({ onClose }) => [
            <Button appearance='primary' onClick={onClose}>
              {t('Got it!')}
            </Button>,
          ]}
          onClose={closeModal}
          title={t('General info about CFD and ETD')}
        >
          <Text isParagraph>
            {t('CFDs (Contracts for difference) and Futures (Exchange traded derivatives)')}
          </Text>
          <br />
          <Text isParagraph>
            {t('Although they share many similarities, they also present several differences')}
          </Text>
        </Modal.Action>
      )
    case GlobalModalsType.RegisterProduct:
      return (
        <RegisterProductModal
          onClose={closeModal}
          onCancel={() => setActiveModal(GlobalModalsType.CancelSwitch)}
          onConfirm={registerProduct}
        />
      )
    case GlobalModalsType.CancelAction:
      return (
        <Modal.Action
          onConfirm={() => {
            closeModal()
            productSwitchClick()
          }}
          onClose={closeModal}
        />
      )
    case GlobalModalsType.CancelSwitch:
      return (
        <Modal.Action
          onClose={() => setActiveModal(GlobalModalsType.ProductSwitch)}
          onConfirm={closeModal}
        />
      )
    case GlobalModalsType.DeleteNotification:
      return (
        <Modal.Action
          title={t('Notifications.Delete Notification')}
          onClose={closeModal}
          onConfirm={modalData.onConfirm}
        >
          <Text>{t('Notifications.This notification will be permanently deleted')}</Text>
        </Modal.Action>
      )
    case GlobalModalsType.BrowserOutdated:
      return <BrowserOutdatedModal />
  }

  return null
}
